import { useBacklogItemLinks } from "@/hooks/useBacklogItemLinks";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import TeamBadgeList from "@/components/shared/TeamBadgeList";
import { resolveAvatarUrl } from "@/lib/avatarUtils";
import { parsePgArray, filterValidUuids } from "@/lib/pgArray";
import ColorBadge from "@/components/shared/ColorBadge";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { Link2 } from "lucide-react";
import { format } from "date-fns";
import type { CSSProperties } from "react";
import { cn } from "@/lib/utils";
import type { DisplayLinkType, ResolvedItemLink } from "@/types/itemLinks";
import type { DropdownOption, UserData } from "@/types/backlog";

interface LinkedItemsSubRowProps {
  itemId: string;
  dropdownData?: {
    statuses: DropdownOption[];
    priorities: DropdownOption[];
    ticketTypes: DropdownOption[];
    profiles: UserData[];
  };
  showCheckbox?: boolean;
  /** Mirrors the parent table's Status column, which is dropped when the list is grouped by status. */
  showStatusColumn?: boolean;
  onNavigateToItem?: (itemId: string) => void;
  linkCounts?: Map<string, number>;
  /** Group-block classes: these rows sit inside the parent row's section. */
  blockClassName?: string;
  /** The section's rail colour, read by `.group-block` off `--group-rail`. */
  railColor?: string;
}

const DISPLAY_TYPE_LABELS: Record<DisplayLinkType, string> = {
  related_to: "Related",
  blocks: "Blocks",
  blocked_by: "Blocked By",
};

const DISPLAY_TYPE_COLORS: Record<DisplayLinkType, string> = {
  related_to: "text-blue-600",
  blocks: "text-amber-600",
  blocked_by: "text-red-600",
};

import { getDisplayName, getUserInitials } from "@/lib/userUtils";

/** Lazily fetches and displays a linked item's own links on hover. */
const LinkCountHoverContent = ({
  linkedItemId,
  parentItemId,
  statuses,
  onNavigateToItem,
}: {
  linkedItemId: string;
  parentItemId: string;
  statuses: DropdownOption[];
  onNavigateToItem?: (itemId: string) => void;
}) => {
  const { data: links = [], isLoading } = useBacklogItemLinks(linkedItemId);
  // Exclude the parent item since we already know about that link
  const otherLinks = links.filter((l) => l.linkedItem.id !== parentItemId);

  if (isLoading) {
    return <div className="animate-pulse h-4 bg-muted rounded w-32" />;
  }

  if (otherLinks.length === 0) {
    return <p className="text-xs text-muted-foreground/70">No other linked items</p>;
  }

  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-muted-foreground mb-1.5">Also linked to:</p>
      {otherLinks.map((link) => {
        const status = statuses.find((s) => s.id === link.linkedItem.status_id);
        return (
          <button
            key={link.linkId}
            type="button"
            className="flex items-center gap-2 py-0.5 w-full text-left hover:bg-muted/50 rounded px-1 -mx-1"
            onClick={(e) => {
              e.stopPropagation();
              onNavigateToItem?.(String(link.linkedItem.ticket_number));
            }}
          >
            <span
              className={`text-[9px] font-semibold uppercase tracking-wider shrink-0 ${DISPLAY_TYPE_COLORS[link.displayType]}`}
            >
              {DISPLAY_TYPE_LABELS[link.displayType]}
            </span>
            <span className="text-xs text-muted-foreground/70 shrink-0">#{link.linkedItem.ticket_number}</span>
            <span className="text-sm text-foreground truncate flex-1">{link.linkedItem.task}</span>
            {status && (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded shrink-0"
                style={{
                  backgroundColor: `${status.color}20`,
                  color: status.color,
                }}
              >
                {status.label}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

const LinkedItemsSubRow = ({
  itemId,
  dropdownData,
  showCheckbox = false,
  showStatusColumn = true,
  onNavigateToItem,
  linkCounts,
  blockClassName,
  railColor,
}: LinkedItemsSubRowProps) => {
  const { data: links = [], isLoading } = useBacklogItemLinks(itemId);
  // These rows are part of the section their parent row sits in, so they carry
  // its block edges too — otherwise expanding a row's links punches a hole in
  // the block's left rail and right border.
  const blockStyle = railColor ? ({ "--group-rail": railColor } as CSSProperties) : undefined;

  if (isLoading) {
    return (
      <tr className={cn("bg-blue-50/40", blockClassName)} style={blockStyle}>
        <td colSpan={99} className="px-4 py-2">
          <div className="animate-pulse h-6 bg-muted rounded w-48" />
        </td>
      </tr>
    );
  }

  if (links.length === 0) return null;

  const resolveAssignees = (assignedToId: string[] | null) => {
    if (!assignedToId || !dropdownData) return [];
    const ids = filterValidUuids(parsePgArray(assignedToId));
    return ids.map((id) => dropdownData.profiles.find((p) => p.id === id)).filter(Boolean) as UserData[];
  };

  const resolveStatus = (statusId: string | null) => {
    if (!statusId || !dropdownData) return null;
    return dropdownData.statuses.find((s) => s.id === statusId) || null;
  };

  const resolvePriority = (priorityId: string | null) => {
    if (!priorityId || !dropdownData) return null;
    return dropdownData.priorities.find((p) => p.id === priorityId) || null;
  };

  const resolveUser = (userId: string) => {
    if (!userId || !dropdownData) return null;
    return dropdownData.profiles.find((p) => p.id === userId) || null;
  };

  const renderLinkedRow = (link: ResolvedItemLink) => {
    const { linkedItem } = link;
    const assignees = resolveAssignees(linkedItem.assigned_to_id);
    const status = resolveStatus(linkedItem.status_id);
    const priority = resolvePriority(linkedItem.priority_id);
    const addedBy = resolveUser(linkedItem.added_by_id);

    return (
      <tr
        key={link.linkId}
        className={cn(
          "bg-blue-50/40 border-b border-border cursor-pointer hover:bg-blue-50/70 transition-colors",
          blockClassName,
        )}
        style={blockStyle}
        onClick={() => onNavigateToItem?.(String(linkedItem.ticket_number))}
      >
        {/* Checkbox placeholder */}
        {showCheckbox && <td className="w-11 px-0.5 py-2" />}

        {/* The relationship label used to have the drag-handle column to sit
            in for admins. That column is gone with manual reordering, so it
            now always rides at the head of the task cell — where it read
            better anyway, next to the thing it describes. */}

        {/* Ticket ID */}
        <td className="w-16 px-1 py-2 text-xs text-muted-foreground text-center font-mono">
          #{linkedItem.ticket_number}
        </td>

        {/* Assigned To */}
        <td className="w-[6.75rem] px-3 py-2 text-sm">
          <div className="flex min-w-0 flex-col gap-1">
            {assignees.length > 0 ? (
              assignees.map((user) => (
                <div key={user.id} className="flex items-center gap-2">
                  <Avatar className="h-6 w-6">
                    {user.avatar_url && <AvatarImage src={resolveAvatarUrl(user.avatar_url)} alt="Avatar" />}
                    <AvatarFallback className="avatar-plate text-xs text-primary dark:text-blue-200 font-medium">
                      {getUserInitials(user)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm">{getDisplayName(user)}</span>
                </div>
              ))
            ) : (
              <span className="text-muted-foreground">-</span>
            )}
            <div className="flex flex-wrap items-center gap-1">
              <TeamBadgeList teamIds={linkedItem.team_ids} assignedTeamId={linkedItem.assigned_team_id} max={2} />
            </div>
          </div>
        </td>

        {/* Task */}
        <td className="w-[32rem] px-4 py-2 text-sm">
          <div className="flex items-center gap-2">
            <span
              className={`text-[9px] font-semibold uppercase tracking-wider shrink-0 ${DISPLAY_TYPE_COLORS[link.displayType]}`}
            >
              {DISPLAY_TYPE_LABELS[link.displayType]}
            </span>
            <span className="min-w-0 flex-1 truncate text-foreground">{linkedItem.task}</span>
            {(() => {
              const ticketType =
                linkedItem.ticket_type_id && dropdownData?.ticketTypes
                  ? dropdownData.ticketTypes.find((t) => t.id === linkedItem.ticket_type_id)
                  : null;
              return ticketType ? <ColorBadge label={ticketType.label} color={ticketType.color} tinted /> : null;
            })()}
            {/* The linked item's own links, in the same place the parent row
                keeps them now that the utility column is gone. */}
            {(() => {
              const count = linkCounts?.get(linkedItem.id) ?? 0;
              if (count === 0) return null;
              return (
                <HoverCard openDelay={300} closeDelay={100}>
                  <HoverCardTrigger asChild>
                    <span
                      className="inline-flex shrink-0 cursor-default items-center gap-0.5 px-1 py-0.5 text-xs text-muted-foreground/70 hover:text-muted-foreground"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Link2 className="h-3 w-3" />
                      <span>{count}</span>
                    </span>
                  </HoverCardTrigger>
                  <HoverCardContent side="right" align="start" className="w-80 p-3">
                    <LinkCountHoverContent
                      linkedItemId={linkedItem.id}
                      parentItemId={itemId}
                      statuses={dropdownData?.statuses || []}
                      onNavigateToItem={onNavigateToItem}
                    />
                  </HoverCardContent>
                </HoverCard>
              );
            })()}
          </div>
        </td>

        {/* Status */}
        {showStatusColumn && (
          <td className="w-32 px-3 py-2 text-sm">
            {status ? (
              <ColorBadge label={status.label} color={status.color} id={status.id} />
            ) : (
              <span className="text-muted-foreground">-</span>
            )}
          </td>
        )}

        {/* Priority */}
        <td className="w-[5.5rem] px-2 py-2 text-sm">
          {priority ? (
            <ColorBadge label={priority.label} color={priority.color} />
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </td>

        {/* Added By */}
        <td className="hidden w-40 px-4 py-2 text-sm 2xl:table-cell">
          <div className="flex min-w-0 items-center gap-2">
            {addedBy ? (
              <>
                <Avatar className="h-6 w-6">
                  {addedBy.avatar_url && <AvatarImage src={resolveAvatarUrl(addedBy.avatar_url)} alt="Avatar" />}
                  <AvatarFallback className="avatar-plate text-xs text-primary dark:text-blue-200 font-medium">
                    {getUserInitials(addedBy)}
                  </AvatarFallback>
                </Avatar>
                <span className="text-foreground">{getDisplayName(addedBy)}</span>
              </>
            ) : (
              <span className="text-muted-foreground">-</span>
            )}
          </div>
        </td>

        {/* Created */}
        <td className="hidden w-28 whitespace-nowrap px-2 py-2 text-center text-sm text-muted-foreground xl:table-cell">
          {linkedItem.created_at ? format(new Date(linkedItem.created_at), "MMM d, yyyy") : "-"}
        </td>

        {/* Last updated. No stale wash here — these rows are context for the
            row above them, not work to triage. */}
        <td className="hidden w-32 whitespace-nowrap px-2 py-2 text-center text-sm text-muted-foreground xl:table-cell">
          {linkedItem.updated_at ? format(new Date(linkedItem.updated_at), "MMM d, yyyy") : "-"}
        </td>

        {/* Actions placeholder — one kebab wide, like the parent row's. */}
        {/* No `border-l` — see BacklogRow's action cell: it doubled the rule. */}
        <td className="w-14 px-2 py-2" />
      </tr>
    );
  };

  return <>{links.map((link) => renderLinkedRow(link))}</>;
};

export default LinkedItemsSubRow;
